import type { ButtonHTMLAttributes, ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { type ButtonSize, type ButtonTone, buttonClass } from './registry';

/**
 * NOTE ON `className`: it is absent from these props on purpose, and TypeScript rejects it.
 *
 * A primitive that accepts `className` is a suggestion, not a base model — the caller can
 * always append one more utility and opt out of the thing the primitive exists to
 * guarantee. This is not hypothetical: Vigilis's filter bar drifted through exactly one
 * prop, `className="h-7"` defeating a touch-target variant, and its search control was
 * hand-rolled fifty-one times afterwards.
 *
 * When a call site needs something these props do not express, the answer is a new prop
 * here — which makes the need visible to everyone — never an escape hatch there.
 */
type NativeButtonProps = Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'className' | 'type'>;

export interface ButtonProps extends NativeButtonProps {
  children: ReactNode;
  tone?: ButtonTone;
  size?: ButtonSize;
  /**
   * `submit` only inside a form that handles it. The default is `button` because an
   * unspecified `type` submits the enclosing form, which is how a "Cancel" beside a form
   * field ends up saving it.
   */
  type?: 'button' | 'submit';
  /**
   * A control on cooldown keeps `aria-disabled` rather than `disabled`, so a keyboard or
   * screen-reader user can still reach it and hear why it will not fire yet. It takes the
   * disabled look without the attribute — the stylesheet matches both.
   */
  pending?: boolean;
}

export function Button({
  children,
  tone = 'primary',
  size = 'md',
  type = 'button',
  pending = false,
  disabled,
  // Spelled out rather than left in `rest`, because the explicit attribute below would
  // otherwise overwrite whatever the caller passed with `undefined` and drop it silently.
  // A caller that states `aria-disabled={false}` wants the attribute present and false —
  // an affordance that stays reachable and flips value, rather than one that appears and
  // vanishes from the accessibility tree as a countdown runs.
  'aria-disabled': ariaDisabled,
  ...rest
}: ButtonProps) {
  return (
    <button
      {...rest}
      type={type}
      className={buttonClass(tone, size)}
      disabled={disabled}
      aria-disabled={ariaDisabled ?? (pending || undefined)}
    >
      {children}
    </button>
  );
}

export interface ButtonLinkProps {
  /** A route in this app. External destinations are not this component's job. */
  to: string;
  children: ReactNode;
  tone?: ButtonTone;
  size?: ButtonSize;
  /**
   * Run as the link is followed — for the one thing a route change cannot do for itself:
   * dismissing the dialog it was pressed inside. Without it the new page loads BEHIND an open
   * dialog and the reader has to close a success message to see what it linked them to.
   *
   * Deliberately not `onClick`: a link's job is the navigation, and a prop named for the event
   * invites putting the navigation itself in there — which is how an anchor loses middle-click
   * and "open in new tab".
   */
  onNavigate?: () => void;
}

/**
 * A route change wearing a button's paint.
 *
 * Rendering these as `<button onClick={navigate(...)}>` would cost the reader middle-click,
 * "open in new tab", and the word "link" from their screen reader — for the sake of looking
 * the same. So the element stays an anchor and only the paint is shared: it reads the same
 * `buttonClass` table as `Button`, so the two cannot drift apart the way the button classes
 * hand-written onto each `<Link>` already had.
 */
export function ButtonLink({
  to,
  children,
  tone = 'primary',
  size = 'md',
  onNavigate,
}: ButtonLinkProps) {
  return (
    <Link to={to} className={buttonClass(tone, size)} onClick={onNavigate}>
      {children}
    </Link>
  );
}

export interface DownloadLinkProps {
  /**
   * An object URL for bytes ALREADY IN THIS PAGE. Never an API path: this element does not go
   * through `apiRequest`, so a URL the browser has to fetch would take its failures to the
   * browser's own error page instead of to a panel the reader can understand.
   */
  href: string;
  /** The name the file is saved under. */
  filename: string;
  children: ReactNode;
  tone?: ButtonTone;
  size?: ButtonSize;
}

/**
 * Save a file the page is already holding.
 *
 * A real anchor with a real `download`, not a `Button` that synthesises a click on a detached
 * one: the anchor is what gives the reader "save as", a middle click, and a control their
 * screen reader announces as a link to a file. A synthesised click has none of that and is
 * silently blocked in more browsing contexts every year.
 *
 * Distinct from `ButtonLink`, whose `to` is a route in this app — routing a `blob:` URL
 * through the router would navigate the SPA to a path that does not exist.
 */
export function DownloadLink({
  href,
  filename,
  children,
  tone = 'ghost',
  size = 'sm',
}: DownloadLinkProps) {
  return (
    <a className={buttonClass(tone, size)} href={href} download={filename}>
      {children}
    </a>
  );
}
